import React, { useEffect, useState } from 'react';
import { 
  Box, 
  Typography, 
  Chip, 
  Button, 
  Avatar,
  Stack,
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  Drawer,
  AppBar,
  Divider,
  Toolbar,
  IconButton,
  Slide,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { DESIGN_TOKENS } from '../../../design-system/tokens';
import { NavigateNext, NavigateBefore, Close, OpenInFull, ExpandMore, CheckCircle } from '@mui/icons-material';
import { TransitionProps } from '@mui/material/transitions';
import { useTranslation } from 'react-i18next';
import { getAnimalPassport } from '../../../services/cattle';
import { apiClientBff } from '../../../services/apiBff';

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

interface MobileInlineResultsProps {
  data: any;
  type: string;
}

interface LotData {
  _key?: string;
  _id?: string;
  _rev?: string;
  farm_id?: string;
  name?: string;
  description?: string | null;
  cattle_tags?: Array<string | { name?: string; tag?: string; identifier?: string }>;
  is_active?: boolean;
  created_at?: string;
  updated_at?: string;
  current_paddock_id?: string;
}

interface AnimalDetails {
  _key?: string;
  _id?: string;
  _rev?: string;
  farm_id?: string;
  current_farm_id?: string;
  name?: string;
  cattle_id?: {
    tag?: string;
    country_code?: string;
    manufacturer_code?: number;
    unique_id?: string;
  };
  breed_code?: string;
  gender?: string;
  birth_date?: string;
  birth_weight_kg?: number;
  current_paddock_id?: string;
  current_status?: string;
  created_at?: string;
  updated_at?: string;
  handling_history?: any[];
}

export const MobileInlineResults: React.FC<MobileInlineResultsProps> = ({ data, type }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [openListFull, setOpenListFull] = useState(false);
  const [openPassportFull, setOpenPassportFull] = useState(false);
  const [openMilkingFull, setOpenMilkingFull] = useState(false);
  const [openLotListFull, setOpenLotListFull] = useState(false);
  const [openLotDetailsFull, setOpenLotDetailsFull] = useState(false);
  const [selectedLot, setSelectedLot] = useState<LotData | null>(null);
  const [lotAnimals, setLotAnimals] = useState<AnimalDetails[]>([]);
  const [isLoadingLotAnimals, setIsLoadingLotAnimals] = useState(false);
  const [lotAnimalsPage, setLotAnimalsPage] = useState(1);
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const [selectedPassport, setSelectedPassport] = useState<any | null>(null);
  const itemsPerPage = 8;
  const lotAnimalsPerPage = 5;
  const { t } = useTranslation(['common', 'animal', 'farm']);
  const detailsHeader = t('animal:table_headers.details', { defaultValue: t('common:details') });

  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));

  const calculateAge = (birthDate: string) => {
    if (!birthDate) return null;
    const today = new Date();
    const birth = new Date(birthDate);
    const months = (today.getFullYear() - birth.getFullYear()) * 12 + (today.getMonth() - birth.getMonth());
    if (months < 1) return '< 1 mês';
    if (months < 12) return `${months} meses`;
    const years = Math.floor(months / 12);
    return `${years} ano${years > 1 ? 's' : ''}`;
  };

  const calculateAgeI18n = (birthDate?: string) => {
    if (!birthDate) return t('animal:not_defined');
    try {
      const birth = new Date(birthDate);
      const today = new Date();
      let months = (today.getFullYear() - birth.getFullYear()) * 12 + (today.getMonth() - birth.getMonth());
      if (today.getDate() < birth.getDate()) {
        months--;
      }
      if (months < 0) months = 0;
      return months === 1
        ? t('animal:time_units.months_one', { count: months })
        : t('animal:time_units.months_other', { count: months });
    } catch {
      return t('animal:not_defined');
    }
  };

  const formatBreed = (breedCode?: string) => {
    if (!breedCode) return t('animal:not_defined');
    return breedCode.charAt(0).toUpperCase() + breedCode.slice(1).toLowerCase();
  };

  const statusChipColor = (
    status?: string,
  ): 'default' | 'success' | 'warning' | 'error' => {
    if (!status) return 'default';
    const s = status.toLowerCase();
    if (
      s.includes('active') ||
      s.includes('ativo') ||
      s.includes('saud') ||
      s.includes('ok')
    )
      return 'success';
    if (s.includes('pend') || s.includes('aten') || s.includes('warning'))
      return 'warning';
    if (
      s.includes('erro') ||
      s.includes('doent') ||
      s.includes('inactive') ||
      s.includes('inativo')
    )
      return 'error';
    return 'default';
  };

  const normalizeGender = (gender?: string) => {
    if (!gender) return '';
    const g = gender.toLowerCase();
    if (g.startsWith('f')) return 'female';
    if (g.startsWith('m')) return 'male';
    return '';
  };

  const getGenderLabel = (gender?: string, fallbackUnknown = true) => {
    const normalized = normalizeGender(gender);
    if (normalized === 'female') return t('animal:gender.female');
    if (normalized === 'male') return t('animal:gender.male');
    if (!fallbackUnknown) return gender || '';
    return gender || t('animal:gender.unknown');
  };

  const getLatestWeightFromHistory = (history: any[] = []) => {
    const valid = history.filter((event) => typeof event?.handling_details?.peso_kg === 'number');
    if (valid.length === 0) return null;
    const sorted = valid.sort((a, b) => {
      const da = new Date(a?.handling_date || 0).getTime();
      const db = new Date(b?.handling_date || 0).getTime();
      return db - da;
    });
    return sorted[0]?.handling_details?.peso_kg ?? null;
  };

  const normalizeI18nKey = (rawKey?: string, prefix?: string) => {
    if (!rawKey) return '';
    const key = rawKey.replace(/^animal:/, '');
    if (prefix && key.startsWith(prefix)) return key.slice(prefix.length);
    return key;
  };

  const translateHandlingType = (handlingTypeCode?: string, title?: string) => {
    const normalizedCode = normalizeI18nKey(handlingTypeCode, 'passport.handling_types.');
    const normalizedTitle = normalizeI18nKey(title, 'passport.handling_types.');
    const key = normalizedCode || normalizedTitle;
    if (key) {
      return t(`animal:passport.handling_types.${key}`, { defaultValue: formatTitle(key) });
    }
    if (title) return title;
    if (!handlingTypeCode) return '-';
    return formatTitle(handlingTypeCode);
  };

  const getStatusLabel = (status?: string) => {
    if (!status) return '';
    const key = `animal:status_values.${status.toLowerCase()}`;
    const translated = t(key);
    return translated !== key ? translated : status;
  };

  const formatTitle = (value?: string) => {
    if (!value) return '';
    return value.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const translateDetailKey = (key: string) => {
    const normalized = normalizeI18nKey(key, 'passport.field_translations.');
    return t(`animal:passport.field_translations.${normalized}`, { defaultValue: formatTitle(normalized || key) });
  };

  const normalizeDetailValue = (value: any) => {
    if (value === null || value === undefined || value === '') return null;
    if (Array.isArray(value)) return value.filter(Boolean).join(', ');
    if (typeof value === 'object') return null;
    return String(value);
  };

  const formatDateSafe = (dateStr: string) => {
    if (!dateStr) return '-';
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) return dateStr;
    if (/^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
      const [y, m, d] = dateStr.split('T')[0].split('-');
      if (y && m && d) return `${d}/${m}/${y}`;
    }
    return dateStr;
  };

  const handleClosePassport = () => {
    setOpenPassportFull(false);
    setSelectedPassport(null);
  };

  const openPassportForAnimal = async (
    animal: any,
    options?: { closeList?: boolean; closeLotDetails?: boolean }
  ) => {
    const identifier = animal?.name;
    const farmId = animal?.farm_id || animal?.current_farm_id;

    if (!identifier || !farmId || loadingId) {
      if (!identifier || !farmId) {
        console.warn('[MobileInlineResults] Missing name or farm_id for animal:', animal);
      }
      return;
    }

    const rowId = String(animal?._key || animal?._id || identifier);
    setLoadingId(rowId);

    try {
      const passportData = await getAnimalPassport(identifier, farmId);
      if (options?.closeList) setOpenListFull(false);
      if (options?.closeLotDetails) setOpenLotDetailsFull(false);
      const rawPassport = passportData as any;
      setSelectedPassport(rawPassport?.data || rawPassport);
      setOpenPassportFull(true);
    } catch (error) {
      console.error('Error fetching animal passport:', error);
    } finally {
      setLoadingId(null);
    }
  };

  const getPassportPayload = () => {
    if (!selectedPassport) return null;
    const animal = selectedPassport?.data || selectedPassport;
    const handlingHistory = animal.handling_history || [];
    const latestWeight = animal.birth_weight_kg || animal.weight || getLatestWeightFromHistory(handlingHistory);
    const formatDate = (dateStr: string) => formatDateSafe(dateStr);
    return { animal, handlingHistory, latestWeight, formatDate };
  };

  const passportPayload = getPassportPayload();

  const renderPassportDialog = (payload: typeof passportPayload) => {
    if (!payload) return null;
    const { animal, handlingHistory, latestWeight, formatDate } = payload;
    return (
      <Dialog
        fullScreen
        open={openPassportFull}
        onClose={handleClosePassport}
        TransitionComponent={Transition}
      >
        <AppBar
          position="sticky"
          elevation={0}
          sx={{
            bgcolor: 'white',
            color: DESIGN_TOKENS.colors.text.primary,
            borderBottom: `1px solid ${DESIGN_TOKENS.colors.neutral[200]}`,
          }}
        >
          <Toolbar sx={{ px: 1, gap: 1, minHeight: { xs: 56, sm: 64 } }}>
            <IconButton
              edge="start"
              color="inherit"
              onClick={handleClosePassport}
              aria-label="close"
              sx={{ width: 44, height: 44 }}
            >
              <Close />
            </IconButton>

            <Avatar
              sx={{
                width: isXs ? 32 : 36,
                height: isXs ? 32 : 36,
                bgcolor: DESIGN_TOKENS.colors.neutral[50],
                border: `1px solid ${DESIGN_TOKENS.colors.neutral[200]}`,
              }}
            >
              <Box
                component="img"
                src="/vaca.svg"
                alt={t('animal:passport.animal_icon')}
                sx={{ width: isXs ? 18 : 20, height: isXs ? 18 : 20 }}
              />
            </Avatar>

            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, lineHeight: 1.2 }} noWrap>
                {animal.name || animal.cattle_id?.tag || t('common:no_id')}
              </Typography>
              <Typography variant="caption" sx={{ color: DESIGN_TOKENS.colors.text.secondary }} noWrap>
                {animal.cattle_id?.tag
                  ? `${t('animal:passport.tag')}: ${animal.cattle_id.tag}`
                  : t('animal:passport.page_title')}
              </Typography>
            </Box>

            {!!animal.current_status && !isXs && (
              <Chip
                size="small"
                variant="outlined"
                label={getStatusLabel(String(animal.current_status))}
                sx={{
                  fontWeight: 700,
                  borderColor: DESIGN_TOKENS.colors.neutral[300],
                  color: DESIGN_TOKENS.colors.neutral[800],
                  bgcolor: 'transparent',
                }}
              />
            )}
          </Toolbar>
        </AppBar>
        
        <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: DESIGN_TOKENS.colors.background.default, minHeight: '100vh', pb: 4 }}>
          <Stack spacing={2.5}>
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: 2,
                border: `1px solid ${DESIGN_TOKENS.colors.neutral[200]}`,
                bgcolor: 'white',
              }}
            >
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: DESIGN_TOKENS.colors.neutral[800] }}>
                    {t('animal:passport.detailed_info')}
                  </Typography>
                  {!!animal.current_status && (
                    <Chip
                      size="small"
                      label={String(animal.current_status)}
                      sx={{
                        fontWeight: 700,
                        height: 26,
                        bgcolor:
                          animal.current_status === 'VENDIDO'
                            ? DESIGN_TOKENS.colors.status.error[50]
                            : DESIGN_TOKENS.colors.status.success[50],
                        color:
                          animal.current_status === 'VENDIDO'
                            ? DESIGN_TOKENS.colors.status.error[700]
                            : DESIGN_TOKENS.colors.status.success[700],
                      }}
                    />
                  )}
                </Box>

                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  <Chip
                    size="small"
                    variant="outlined"
                    icon={<Box component="img" src="/vaca.svg" alt={t('animal:passport.animal_icon')} sx={{ width: 16, height: 16 }} />}
                    label={animal.breed_code || animal.breed || t('animal:passport.not_defined')}
                    sx={{ fontWeight: 600 }}
                  />
                  <Chip
                    size="small"
                    variant="outlined"
                    label={getGenderLabel(animal.gender)}
                    sx={{ fontWeight: 600 }}
                  />
                  <Chip
                    size="small"
                    variant="outlined"
                    label={calculateAge(animal.birth_date) || t('animal:passport.not_defined')}
                    sx={{ fontWeight: 600 }}
                  />
                </Stack>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">{t('animal:passport.tag')}</Typography>
                    <Typography variant="body2" fontWeight={600}>{animal.cattle_id?.tag || t('animal:passport.not_defined')}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">{t('animal:passport.fields.birth_date')}</Typography>
                    <Typography variant="body2" fontWeight={600}>{formatDate(animal.birth_date) || t('animal:passport.not_defined')}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">{t('animal:passport.fields.weight')}</Typography>
                    <Typography variant="body2" fontWeight={600}>
                      {latestWeight ? `${latestWeight}kg` : t('animal:passport.not_defined')}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary" display="block">{t('animal:passport.fields.location')}</Typography>
                    <Typography variant="body2" fontWeight={600}>{animal.current_paddock_id || animal.location || t('animal:passport.not_defined')}</Typography>
                  </Box>
                </Box>
              </Stack>
            </Paper>

            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: 2,
                border: `1px solid ${DESIGN_TOKENS.colors.neutral[200]}`,
                bgcolor: 'white',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: DESIGN_TOKENS.colors.neutral[800] }}>
                  {t('animal:passport.handling_history.title')}
                </Typography>
                <Chip
                  size="small"
                  variant="outlined"
                  label={`${handlingHistory.length} ${t('animal:passport.handling_history.records_found')}`}
                  sx={{ fontWeight: 600 }}
                />
              </Box>

              {handlingHistory.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {t('animal:passport.handling_history.no_records')}
                </Typography>
              ) : (
                <Stack spacing={2}>
                  {[...handlingHistory].reverse().map((event: any, index: number) => {
                    const isLaboratoryExam = event.handling_type_code === 'laboratory_exam' || event?.handling_details?.exam_type === 'mastitis_test';
                    const details = buildHandlingDetails(event, formatDate);
                    return (
                    <Box key={`${event.handling_date}-${index}`} sx={{ display: 'flex', gap: 2 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 32 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: DESIGN_TOKENS.colors.primary[500], mt: 0.5 }} />
                        {index < handlingHistory.length - 1 && (
                          <Box sx={{ width: 2, flex: 1, bgcolor: DESIGN_TOKENS.colors.neutral[200], mt: 0.5 }} />
                        )}
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {formatDate(event.handling_date)}
                        </Typography>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                          {translateHandlingType(event.handling_type_code, event.title)}
                        </Typography>
                        {isLaboratoryExam ? (
                          <Accordion elevation={0} sx={{ mt: 1, border: `1px solid ${DESIGN_TOKENS.colors.neutral[200]}`, borderRadius: 2, bgcolor: 'white' }}>
                            <AccordionSummary expandIcon={<ExpandMore />} sx={{ px: 2 }}>
                              <Box sx={{ width: '100%' }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: DESIGN_TOKENS.colors.primary[900] }}>
                                  {translateHandlingType(event.handling_type_code, event.title)}
                                </Typography>
                                <Typography variant="caption" sx={{ color: DESIGN_TOKENS.colors.text.secondary, display: 'block' }}>
                                  {translateDetailKey('exam_date')}: {event?.handling_details?.exam_date ? formatDate(event.handling_details.exam_date) : formatDate(event.handling_date)}
                                </Typography>
                              </Box>
                            </AccordionSummary>
                            <AccordionDetails sx={{ px: 2, pt: 0 }}>
                              {details.length > 0 && (
                                <Box sx={{ mt: 1 }}>
                                  <Stack spacing={0.75}>
                                    {details.map((detail, detailIndex) => (
                                      <Box key={`${detail.label}-${detailIndex}`}>
                                        <Typography variant="caption" sx={{ color: DESIGN_TOKENS.colors.text.secondary, fontWeight: 600 }}>
                                          {detail.label}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: DESIGN_TOKENS.colors.text.primary }}>
                                          {detail.value}
                                        </Typography>
                                      </Box>
                                    ))}
                                  </Stack>
                                </Box>
                              )}
                            </AccordionDetails>
                          </Accordion>
                        ) : (
                          details.length > 0 && (
                            <Box sx={{ mt: 1 }}>
                              <Stack spacing={0.75}>
                                {details.map((detail, detailIndex) => (
                                  <Box key={`${detail.label}-${detailIndex}`}>
                                    <Typography variant="caption" sx={{ color: DESIGN_TOKENS.colors.text.secondary, fontWeight: 600 }}>
                                      {detail.label}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: DESIGN_TOKENS.colors.text.primary }}>
                                      {detail.value}
                                    </Typography>
                                  </Box>
                                ))}
                              </Stack>
                            </Box>
                          )
                        )}
                      </Box>
                    </Box>
                  );
                  })}
                </Stack>
              )}
            </Paper>
          </Stack>
        </Box>
      </Dialog>
    );
  };

  useEffect(() => {
    const fetchLotAnimals = async () => {
      if (!openLotDetailsFull) return;
      const lot = selectedLot;
      const tags = Array.isArray(lot?.cattle_tags) ? lot?.cattle_tags : [];
      const names = tags
        .map((ct) => {
          if (typeof ct === 'string') return ct;
          return ct?.name || ct?.tag || ct?.identifier;
        })
        .filter(Boolean);

      if (!lot?.farm_id || names.length === 0) {
        setLotAnimals([]);
        return;
      }

      setIsLoadingLotAnimals(true);
      try {
        const requestPayload = {
          bind_vars: {
            '@collection': 'Cattle',
            '@collectionHandling': 'Handling',
            cattle_tags: names,
            farm_id: lot?.farm_id,
          },
          query:
            'FOR t IN @cattle_tags LET doc = FIRST(FOR c IN @@collection FILTER LOWER(c.name) == LOWER(t) AND c.farm_id == @farm_id RETURN c) FILTER doc != null LET handling_history = (FOR h IN @@collectionHandling FILTER LOWER(h.cattle_tag) == LOWER(t) AND h.farm_id == @farm_id RETURN h) RETURN MERGE(doc, { handling_history })',
        };
        const response: any = await apiClientBff.post('/cattle/search', requestPayload);
        const results = Array.isArray(response?.data) ? response.data : [];
        setLotAnimals(results);
      } catch (e) {
        setLotAnimals([]);
      } finally {
        setIsLoadingLotAnimals(false);
      }
    };

    fetchLotAnimals();
  }, [openLotDetailsFull, selectedLot?.farm_id, selectedLot?.cattle_tags]);

  const buildHandlingDetails = (event: any, formatDateLocal: (dateStr: string) => string): Array<{ label: string; value: string }> => {
    const details: Array<{ label: string; value: string }> = [];
    const d = event?.handling_details || {};
    const add = (label: string, value: any) => {
      const normalized = normalizeDetailValue(value);
      if (normalized) details.push({ label, value: normalized });
    };
    if (typeof d.peso_kg === 'number') {
      add(t('animal:passport.handling_history.weight'), `${d.peso_kg} ${t('animal:passport.handling_history.weight_kg')}`);
    }
    if (typeof d.milk_quantity_liters === 'number') {
      add(translateDetailKey('milk_production'), `${d.milk_quantity_liters} L`);
    }
    add(translateDetailKey('destination_tank'), d.destination_tank);
    add(translateDetailKey('performed_by'), event?.performed_by);
    add(translateDetailKey('procedure_performed'), d.procedure_performed);
    add(translateDetailKey('products_used'), d.products_used);
    add(translateDetailKey('disease'), d.disease);
    if (typeof d.dosage_ml !== 'undefined') add(translateDetailKey('dosage_ml'), `${d.dosage_ml} ml`);
    add(translateDetailKey('product_name'), d.product_name);
    if (typeof event?.cost !== 'undefined') add(translateDetailKey('cost'), event.cost);
    add(translateDetailKey('feed_type'), d.feed_type);
    if (typeof d.quantity_kg !== 'undefined') add(translateDetailKey('quantity_kg'), `${d.quantity_kg} kg`);
    add(translateDetailKey('bull'), d.touro_id);
    add(translateDetailKey('status'), formatTitle(d.status));
    add(translateDetailKey('status_pos'), formatTitle(d.status_pos));
    add(translateDetailKey('result'), formatTitle(d.resultado_diagnostico));
    add(translateDetailKey('method'), formatTitle(d.metodo_diagnostico));
    if (d.collection_date) add(translateDetailKey('collection_date'), formatDateLocal(d.collection_date));
    if (d.exam_date) add(translateDetailKey('exam_date'), formatDateLocal(d.exam_date));
    add(translateDetailKey('plate_id'), d.plate_id);
    if (d.results) {
      const r = d.results;
      add(translateDetailKey('result'), r.exam_result || r.result);
      add(translateDetailKey('teats_affected'), r.teatsAffected);
      add(translateDetailKey('milk_aspect'), r.milkAspect);
      add(translateDetailKey('udder_signs'), r.udderSigns);
      add(translateDetailKey('staph_primary'), r.staphPrimary);
      add(translateDetailKey('staph_secondary'), r.staphSecondary);
      add(translateDetailKey('strep_primary'), r.strepPrimary);
      add(translateDetailKey('strep_secondary'), r.strepSecondary);
      add(translateDetailKey('general_primary'), r.generalPrimary);
      add(translateDetailKey('general_secondary'), r.generalSecondary);
      add(translateDetailKey('observation'), r.observation);
    }
    add(translateDetailKey('notes'), event?.notes);
    return details;
  };

  // Renderização específica para Lista de Animais
  if (type === 'animal_list' || type === 'animal-list') {
    const animals = Array.isArray(data) ? data : (data?.data || []);
    
    if (!animals || animals.length === 0) return null;

    // Lógica de Paginação (Inline)
    const totalPages = Math.ceil(animals.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const currentAnimals = animals.slice(startIndex, startIndex + itemsPerPage);

    const handleNext = () => {
      if (currentPage < totalPages) setCurrentPage(p => p + 1);
    };

    const handlePrev = () => {
      if (currentPage > 1) setCurrentPage(p => p - 1);
    };

    const handleOpenFull = () => setOpenListFull(true);
    const handleCloseFull = () => setOpenListFull(false);

    const handleRowClick = (animal: any) => openPassportForAnimal(animal, { closeList: true });

    if (passportPayload) {
      const { animal, latestWeight, formatDate } = passportPayload;

      return (
        <Box sx={{ mt: 1.5, width: '100%', maxWidth: '90vw' }}>
          <Paper 
            elevation={0} 
            sx={{ 
              p: 2, 
              border: `1px solid ${DESIGN_TOKENS.colors.neutral[200]}`, 
              borderRadius: 2,
              bgcolor: 'white'
            }}
          >
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" sx={{ color: DESIGN_TOKENS.colors.primary[900], fontWeight: 700, fontSize: '1.1rem' }}>
                {animal.name || animal.cattle_id?.tag || t('common:no_id')}
              </Typography>
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">{t('animal:passport.fields.breed')}</Typography>
                <Typography variant="body2" fontWeight={500}>{animal.breed_code || animal.breed || '-'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">{t('animal:passport.fields.gender')}</Typography>
                <Typography variant="body2" fontWeight={500}>
                  {getGenderLabel(animal.gender, false) || '-'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">{t('animal:passport.fields.age')}</Typography>
                <Typography variant="body2" fontWeight={500}>{calculateAge(animal.birth_date) || '-'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" display="block">{t('animal:passport.fields.weight')}</Typography>
                <Typography variant="body2" fontWeight={500}>
                  {latestWeight ? `${latestWeight}kg` : '-'}
                </Typography>
              </Box>
            </Box>
          </Paper>

          {renderPassportDialog(passportPayload)}
        </Box>
      );
    }

    return (
      <Box sx={{ mt: 1.5, width: '100%', maxWidth: '90vw', overflowX: 'auto' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, px: 0.5 }}>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
            {t('common:results', { count: animals.length })}
          </Typography>
          <Button 
            size="small" 
            onClick={handleOpenFull}
            startIcon={<OpenInFull sx={{ fontSize: 14 }} />}
            sx={{ fontSize: '0.7rem', textTransform: 'none', py: 0, minHeight: 0 }}
          >
            {t('common:view_details')}
          </Button>
        </Box>
        
        <TableContainer 
          component={Paper} 
          elevation={0} 
          sx={{ 
            border: `1px solid ${DESIGN_TOKENS.colors.neutral[200]}`,
            borderRadius: 2,
            maxHeight: 400,
            bgcolor: 'white',
            overflowX: 'hidden'
          }}
        >
          <Table size="small" stickyHeader sx={{ width: '100%' }}>
            <TableHead>
              <TableRow sx={{ '& th': { bgcolor: DESIGN_TOKENS.colors.neutral[50], fontWeight: 700, fontSize: '0.75rem', py: 1, px: 1 } }}>
                <TableCell>{t('animal:table_headers.animal')}</TableCell>
                <TableCell align="right">{t('animal:table_headers.breed')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {currentAnimals.map((animal: any, index: number) => {
                const rowId = String(animal?._key || animal?._id || animal?.name || index);
                const isLoading = loadingId === rowId;
                return (
                <TableRow 
                  key={rowId}
                  hover
                  onClick={() => handleRowClick(animal)}
                  sx={{ 
                    cursor: isLoading ? 'progress' : 'pointer',
                    opacity: isLoading ? 0.7 : 1,
                    backgroundColor: isLoading ? DESIGN_TOKENS.colors.neutral[100] : 'inherit',
                    '&:hover': { backgroundColor: DESIGN_TOKENS.colors.neutral[50] },
                    '&:last-child td': { border: 0 },
                    '& td': { fontSize: '0.75rem', py: 1.5, px: 1 }
                  }}
                >
                  <TableCell>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.8rem', color: DESIGN_TOKENS.colors.primary[900] }}>
                      {animal.name || animal.cattle_id?.tag || animal.tag || t('common:no_id')}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                      {animal.breed_code || animal.breed || '-'}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                      {getGenderLabel(animal.gender, false)}
                      {animal.weight ? ` • ${animal.weight}kg` : ''}
                    </Typography>
                  </TableCell>
                </TableRow>
              );
            })}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Controles de Paginação */}
        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1, px: 0.5 }}>
            <Button 
              size="small" 
              onClick={handlePrev} 
              disabled={currentPage === 1}
              startIcon={<NavigateBefore />}
              sx={{ textTransform: 'none', fontSize: '0.75rem', minWidth: 'auto', p: 0.5 }}
            >
              {t('common:previous')}
            </Button>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
              {currentPage} / {totalPages}
            </Typography>
            <Button 
              size="small" 
              onClick={handleNext} 
              disabled={currentPage === totalPages}
              endIcon={<NavigateNext />}
              sx={{ textTransform: 'none', fontSize: '0.75rem', minWidth: 'auto', p: 0.5 }}
            >
              {t('common:next')}
            </Button>
          </Box>
        )}

        {/* Modal Fullscreen Detalhado */}
        <Dialog
          fullScreen
          open={openListFull}
          onClose={handleCloseFull}
          TransitionComponent={Transition}
        >
          <AppBar 
            position="sticky"
            elevation={0}
            sx={{ 
              bgcolor: 'white', 
              color: DESIGN_TOKENS.colors.text.primary,
              borderBottom: `1px solid ${DESIGN_TOKENS.colors.neutral[200]}`,
            }}
          >
            <Toolbar sx={{ px: 1, gap: 1, minHeight: { xs: 56, sm: 64 } }}>
              <IconButton
                edge="start"
                color="inherit"
                onClick={handleCloseFull}
                aria-label="close"
                sx={{ width: 44, height: 44 }}
              >
                <Close />
              </IconButton>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, flex: 1 }}>
                {t('animal:list_title_full')}
              </Typography>
            </Toolbar>
          </AppBar>
          <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: DESIGN_TOKENS.colors.background.default, minHeight: '100vh' }}>
             <TableContainer 
                component={Paper} 
                elevation={0} 
                sx={{ 
                  width: '100%', 
                  overflowX: 'auto',
                  border: `1px solid ${DESIGN_TOKENS.colors.neutral[200]}`,
                  borderRadius: 2
                }}
              >
                <Table stickyHeader sx={{ minWidth: { xs: 520, sm: 720 } }}>
                  <TableHead>
                    <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: DESIGN_TOKENS.colors.neutral[50], color: DESIGN_TOKENS.colors.text.secondary } }}>
                      <TableCell>{t('animal:table_headers.animal')}</TableCell>
                      <TableCell>{detailsHeader}</TableCell>
                      <TableCell>{t('animal:table_headers.location')}</TableCell>
                      <TableCell>{t('animal:table_headers.weight')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {animals.map((animal: any, index: number) => {
                      const rowId = String(animal?._key || animal?._id || animal?.name || index);
                      const isLoading = loadingId === rowId;
                      return (
                      <TableRow
                        key={rowId}
                        hover
                        onClick={() => handleRowClick(animal)}
                        sx={{
                          cursor: isLoading ? 'progress' : 'pointer',
                          opacity: isLoading ? 0.7 : 1,
                          backgroundColor: isLoading ? DESIGN_TOKENS.colors.neutral[100] : 'inherit',
                          '&:hover': { backgroundColor: DESIGN_TOKENS.colors.neutral[50] },
                          '& td': { borderColor: DESIGN_TOKENS.colors.neutral[100] }
                        }}
                      >
                        <TableCell>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: DESIGN_TOKENS.colors.primary[900] }}>
                             {animal.name || animal.cattle_id?.tag || animal.tag || t('common:no_id')}
                          </Typography>
                        </TableCell>
                        <TableCell>
                           <Typography variant="body2" fontWeight={500}>{animal.breed_code || animal.breed || '-'}</Typography>
                           <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                             {animal.gender === 'F' ? t('animal:gender.female') : animal.gender === 'M' ? t('animal:gender.male') : (animal.gender || t('animal:gender.unknown'))}
                             {animal.birth_date ? ` • ${calculateAge(animal.birth_date)}` : ''}
                           </Typography>
                        </TableCell>
                        <TableCell>
                           <Typography variant="body2">{animal.current_paddock_id || animal.location || t('common:not_registered')}</Typography>
                        </TableCell>
                        <TableCell>
                           <Typography variant="body2" sx={{ fontWeight: 600 }}>
                             {animal.birth_weight_kg || animal.weight ? `${animal.birth_weight_kg || animal.weight}kg` : t('common:not_registered')}
                           </Typography>
                        </TableCell>
                      </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
             </TableContainer>
             <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                   {t('common:results_count', { count: animals.length })}
                </Typography>
             </Box>
          </Box>
        </Dialog>

      </Box>
    );
  }

  // Renderização específica para Passaporte do Animal (Ficha Única)
  if (type === 'animal_passport' || type === 'animal-details' || type === 'animal_detail' || type === 'animal-detail') {
    // O objeto pode vir como data ou data.data dependendo da resposta
    const animal = data?.data || data;
    
    if (!animal) return null;

    const handleOpenFull = () => setOpenPassportFull(true);
    const handleCloseFull = () => setOpenPassportFull(false);
    
    const handlingHistory = animal.handling_history || [];
    
    // Função auxiliar para data
    const formatDate = (dateStr: string) => formatDateSafe(dateStr);

    return (
      <Box sx={{ mt: 1.5, width: '100%', maxWidth: '90vw' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, px: 0.5 }}>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
            {t('animal:passport.title')}
          </Typography>
          <Button 
            size="small" 
            onClick={handleOpenFull}
            startIcon={<OpenInFull sx={{ fontSize: 14 }} />}
            sx={{ fontSize: '0.7rem', textTransform: 'none', py: 0, minHeight: 0 }}
          >
            {t('common:view_details')}
          </Button>
        </Box>

        <Paper 
          elevation={0} 
          sx={{ 
            p: 2, 
            border: `1px solid ${DESIGN_TOKENS.colors.neutral[200]}`, 
            borderRadius: 2,
            bgcolor: 'white'
          }}
        >
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" sx={{ color: DESIGN_TOKENS.colors.primary[900], fontWeight: 700, fontSize: '1.1rem' }}>
              {animal.name || animal.cattle_id?.tag || t('common:no_id')}
            </Typography>
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">{t('animal:passport.fields.breed')}</Typography>
              <Typography variant="body2" fontWeight={500}>{animal.breed_code || animal.breed || '-'}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">{t('animal:passport.fields.gender')}</Typography>
              <Typography variant="body2" fontWeight={500}>
                {animal.gender === 'F' ? t('animal:gender.female') : animal.gender === 'M' ? t('animal:gender.male') : (animal.gender || '-')}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">{t('animal:passport.fields.age')}</Typography>
              <Typography variant="body2" fontWeight={500}>{calculateAge(animal.birth_date) || '-'}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary" display="block">{t('animal:passport.fields.weight')}</Typography>
              <Typography variant="body2" fontWeight={500}>
                {animal.birth_weight_kg || animal.weight ? `${animal.birth_weight_kg || animal.weight}kg` : '-'}
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* Modal Fullscreen Detalhado (Passaporte) */}
        <Dialog
          fullScreen
          open={openPassportFull}
          onClose={handleCloseFull}
          TransitionComponent={Transition}
        >
          <AppBar
            position="sticky"
            elevation={0}
            sx={{
              bgcolor: 'white',
              color: DESIGN_TOKENS.colors.text.primary,
              borderBottom: `1px solid ${DESIGN_TOKENS.colors.neutral[200]}`,
            }}
          >
            <Toolbar sx={{ px: 1, gap: 1, minHeight: { xs: 56, sm: 64 } }}>
              <IconButton
                edge="start"
                color="inherit"
                onClick={handleCloseFull}
                aria-label="close"
                sx={{ width: 44, height: 44 }}
              >
                <Close />
              </IconButton>

              <Avatar
                sx={{
                  width: isXs ? 32 : 36,
                  height: isXs ? 32 : 36,
                  bgcolor: DESIGN_TOKENS.colors.neutral[50],
                  border: `1px solid ${DESIGN_TOKENS.colors.neutral[200]}`,
                }}
              >
                <Box
                  component="img"
                  src="/vaca.svg"
                  alt={t('animal:passport.animal_icon')}
                  sx={{ width: isXs ? 18 : 20, height: isXs ? 18 : 20 }}
                />
              </Avatar>

              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, lineHeight: 1.2 }} noWrap>
                  {animal.name || animal.cattle_id?.tag || t('common:no_id')}
                </Typography>
                <Typography variant="caption" sx={{ color: DESIGN_TOKENS.colors.text.secondary }} noWrap>
                  {animal.cattle_id?.tag
                    ? `${t('animal:passport.tag')}: ${animal.cattle_id.tag}`
                    : t('animal:passport.page_title')}
                </Typography>
              </Box>

              {!!animal.current_status && !isXs && (
                <Chip
                  size="small"
                  variant="outlined"
                  label={String(animal.current_status)}
                  sx={{
                    fontWeight: 700,
                    borderColor: DESIGN_TOKENS.colors.neutral[300],
                    color: DESIGN_TOKENS.colors.neutral[800],
                    bgcolor: 'transparent',
                  }}
                />
              )}
            </Toolbar>
          </AppBar>
          
          <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: DESIGN_TOKENS.colors.background.default, minHeight: '100vh', pb: 4 }}>
            <Stack spacing={2.5}>
              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: 2,
                  border: `1px solid ${DESIGN_TOKENS.colors.neutral[200]}`,
                  bgcolor: 'white',
                }}
              >
                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, color: DESIGN_TOKENS.colors.neutral[800] }}>
                      {t('animal:passport.detailed_info')}
                    </Typography>
                    {!!animal.current_status && (
                      <Chip
                        size="small"
                        label={getStatusLabel(String(animal.current_status))}
                        sx={{
                          fontWeight: 700,
                          height: 26,
                          bgcolor:
                            animal.current_status === 'VENDIDO'
                              ? DESIGN_TOKENS.colors.status.error[50]
                              : DESIGN_TOKENS.colors.status.success[50],
                          color:
                            animal.current_status === 'VENDIDO'
                              ? DESIGN_TOKENS.colors.status.error[700]
                              : DESIGN_TOKENS.colors.status.success[700],
                        }}
                      />
                    )}
                  </Box>

                  <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                    <Chip
                      size="small"
                      variant="outlined"
                      icon={<Box component="img" src="/vaca.svg" alt={t('animal:passport.animal_icon')} sx={{ width: 16, height: 16 }} />}
                      label={animal.breed_code || animal.breed || t('animal:passport.not_defined')}
                      sx={{ fontWeight: 600 }}
                    />
                    <Chip
                      size="small"
                      variant="outlined"
                      label={
                        animal.gender === 'F'
                          ? t('animal:gender.female')
                          : animal.gender === 'M'
                            ? t('animal:gender.male')
                            : (animal.gender || t('animal:passport.not_defined'))
                      }
                      sx={{ fontWeight: 600 }}
                    />
                    <Chip
                      size="small"
                      variant="outlined"
                      label={calculateAge(animal.birth_date) || t('animal:passport.not_defined')}
                      sx={{ fontWeight: 600 }}
                    />
                  </Stack>

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                      gap: 2,
                    }}
                  >
                    <Box>
                      <Typography variant="caption" sx={{ color: DESIGN_TOKENS.colors.text.secondary, display: 'block' }}>
                        {t('animal:passport.tag')}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: DESIGN_TOKENS.colors.primary[900] }}>
                        {animal.cattle_id?.tag || animal.name || '-'}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="caption" sx={{ color: DESIGN_TOKENS.colors.text.secondary, display: 'block' }}>
                        {t('animal:passport.fields.birth_date')}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {formatDate(animal.birth_date)}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="caption" sx={{ color: DESIGN_TOKENS.colors.text.secondary, display: 'block' }}>
                        {t('animal:passport.fields.weight')}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {animal.birth_weight_kg || animal.weight ? `${animal.birth_weight_kg || animal.weight}kg` : '-'}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography variant="caption" sx={{ color: DESIGN_TOKENS.colors.text.secondary, display: 'block' }}>
                        {t('animal:passport.fields.location')}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {animal.current_paddock_id || animal.location || '-'}
                      </Typography>
                    </Box>
                  </Box>
                </Stack>
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  p: 2.5,
                  borderRadius: 2,
                  border: `1px solid ${DESIGN_TOKENS.colors.neutral[200]}`,
                  bgcolor: 'white',
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: DESIGN_TOKENS.colors.neutral[800] }}>
                    {t('animal:passport.handling_history.title')}
                  </Typography>
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`${handlingHistory.length} ${t('animal:passport.handling_history.records_found')}`}
                    sx={{ fontWeight: 700 }}
                  />
                </Box>

                {handlingHistory.length === 0 ? (
                  <Box sx={{ p: 2, borderRadius: 2, bgcolor: DESIGN_TOKENS.colors.neutral[50], border: `1px dashed ${DESIGN_TOKENS.colors.neutral[200]}` }}>
                    <Typography variant="body2" sx={{ color: DESIGN_TOKENS.colors.text.secondary, textAlign: 'center' }}>
                      {t('animal:passport.handling_history.no_records')}
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ position: 'relative', pl: 2, borderLeft: `2px solid ${DESIGN_TOKENS.colors.neutral[200]}` }}>
                    <Stack spacing={1.5}>
                      {handlingHistory.map((event: any, idx: number) => {
                        const isLaboratoryExam = event.handling_type_code === 'laboratory_exam' || event?.handling_details?.exam_type === 'mastitis_test';
                        const details = buildHandlingDetails(event, formatDate);
                        return (
                        <Box key={idx} sx={{ position: 'relative' }}>
                          <Box
                            sx={{
                              position: 'absolute',
                              left: -21,
                              top: 10,
                              width: 10,
                              height: 10,
                              borderRadius: '50%',
                              bgcolor: DESIGN_TOKENS.colors.primary[500],
                              border: `2px solid white`,
                            }}
                          />

                          <Typography variant="caption" sx={{ color: DESIGN_TOKENS.colors.text.secondary, mb: 0.5, display: 'block' }}>
                            {formatDate(event.handling_date)}
                          </Typography>

                          {isLaboratoryExam ? (
                            <Accordion elevation={0} sx={{ border: `1px solid ${DESIGN_TOKENS.colors.neutral[200]}`, borderRadius: 2, bgcolor: 'white' }}>
                              <AccordionSummary expandIcon={<ExpandMore />} sx={{ px: 2 }}>
                                <Box sx={{ width: '100%' }}>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: DESIGN_TOKENS.colors.primary[900] }}>
                                    {translateHandlingType(event.handling_type_code, event.title)}
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: DESIGN_TOKENS.colors.text.secondary, display: 'block' }}>
                                    {translateDetailKey('exam_date')}: {event?.handling_details?.exam_date ? formatDate(event.handling_details.exam_date) : formatDate(event.handling_date)}
                                  </Typography>
                                </Box>
                              </AccordionSummary>
                              <AccordionDetails sx={{ px: 2, pt: 0 }}>
                                {details.length > 0 && (
                                  <Box sx={{ mt: 1 }}>
                                    <Stack spacing={0.75}>
                                      {details.map((detail, detailIndex) => (
                                        <Box key={`${detail.label}-${detailIndex}`}>
                                          <Typography variant="caption" sx={{ color: DESIGN_TOKENS.colors.text.secondary, fontWeight: 600 }}>
                                            {detail.label}
                                          </Typography>
                                          <Typography variant="body2" sx={{ color: DESIGN_TOKENS.colors.text.primary }}>
                                            {detail.value}
                                          </Typography>
                                        </Box>
                                      ))}

                                      {/* Tratamento no Mobile */}
                                      {event.handling_details?.treatment && (
                                        <Box sx={{ mt: 1.5, p: 1.5, bgcolor: DESIGN_TOKENS.colors.neutral[50], borderRadius: 1 }}>
                                          <Typography variant="caption" sx={{ color: DESIGN_TOKENS.colors.brand.primary, fontWeight: 700, mb: 1, display: 'block' }}>
                                            {t('animal:passport.treatment.recommended', { defaultValue: 'Tratamento Recomendado' })}
                                          </Typography>
                                          
                                          <Stack spacing={1}>
                                            {event.handling_details.treatment.situation && (
                                              <Box>
                                                <Typography variant="caption" sx={{ color: DESIGN_TOKENS.colors.text.secondary, fontWeight: 600 }}>
                                                  {t('animal:passport.treatment.situation', { defaultValue: 'Situação' })}:
                                                </Typography>
                                                <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                                                  {event.handling_details.treatment.situation}
                                                </Typography>
                                              </Box>
                                            )}
                                            {event.handling_details.treatment.mainTreatment && (
                                              <Box>
                                                <Typography variant="caption" sx={{ color: DESIGN_TOKENS.colors.text.secondary, fontWeight: 600 }}>
                                                  {t('animal:passport.treatment.main', { defaultValue: 'Tratamento Principal' })}:
                                                </Typography>
                                                <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                                                  {event.handling_details.treatment.mainTreatment}
                                                </Typography>
                                              </Box>
                                            )}
                                            {event.handling_details.treatment.recommendedAntibiotics && (
                                              <Box>
                                                <Typography variant="caption" sx={{ color: DESIGN_TOKENS.colors.text.secondary, fontWeight: 600 }}>
                                                  {t('animal:passport.treatment.antibiotics', { defaultValue: 'Antibióticos Recomendados' })}:
                                                </Typography>
                                                <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                                                  {event.handling_details.treatment.recommendedAntibiotics}
                                                </Typography>
                                              </Box>
                                            )}
                                          </Stack>
                                        </Box>
                                      )}
                                    </Stack>
                                  </Box>
                                )}
                              </AccordionDetails>
                            </Accordion>
                          ) : (
                          <Paper
                            elevation={0}
                            sx={{
                              p: 2,
                              border: `1px solid ${DESIGN_TOKENS.colors.neutral[200]}`,
                              borderRadius: 2,
                              bgcolor: 'white',
                            }}
                          >
                            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: DESIGN_TOKENS.colors.primary[900] }}>
                              {translateHandlingType(event.handling_type_code, event.title)}
                            </Typography>
                            {details.length > 0 && (
                              <Box sx={{ mt: 1 }}>
                                <Stack spacing={0.75}>
                                  {details.map((detail, detailIndex) => (
                                    <Box key={`${detail.label}-${detailIndex}`}>
                                      <Typography variant="caption" sx={{ color: DESIGN_TOKENS.colors.text.secondary, fontWeight: 600 }}>
                                        {detail.label}
                                      </Typography>
                                      <Typography variant="body2" sx={{ color: DESIGN_TOKENS.colors.text.primary }}>
                                        {detail.value}
                                      </Typography>
                                    </Box>
                                  ))}
                                </Stack>
                              </Box>
                            )}
                          </Paper>
                          )}
                        </Box>
                      );
                      })}
                    </Stack>
                  </Box>
                )}
              </Paper>
            </Stack>
          </Box>
        </Dialog>
        {renderPassportDialog(passportPayload)}
      </Box>
    );
  }

  // Renderização específica para Histórico de Ordenha (Mobile)
  if (type === 'animalMilkingHistory') {
    // Tenta extrair os dados de vários locais possíveis para garantir compatibilidade
    const milkingData = data?.animalMilkingData || data?.data || data;
    const records = Array.isArray(milkingData) ? milkingData : (milkingData?.data || []);
    
    if (!records || !Array.isArray(records) || records.length === 0) return null;

    const handleOpenFull = () => setOpenMilkingFull(true);
    const handleCloseFull = () => setOpenMilkingFull(false);
    
    const formatDate = (dateStr: string) => formatDateSafe(dateStr);

    const compactRecords = records.slice(0, 3);
    const hasMore = records.length > 3;

    return (
      <Box sx={{ mt: 1.5, width: '100%', maxWidth: '90vw' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, px: 0.5 }}>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
            {t('dairy:animalMilkingHistory.milkingRecords')}
          </Typography>
          <Button 
            size="small" 
            onClick={handleOpenFull}
            startIcon={<OpenInFull sx={{ fontSize: 14 }} />}
            sx={{ fontSize: '0.7rem', textTransform: 'none', py: 0, minHeight: 0 }}
          >
            {t('common:view_details')}
          </Button>
        </Box>

        <Paper 
          elevation={0} 
          sx={{ 
            p: 2, 
            border: `1px solid ${DESIGN_TOKENS.colors.neutral[200]}`, 
            borderRadius: 2,
            bgcolor: 'white'
          }}
        >
          <Stack spacing={1.5}>
            {compactRecords.map((record: any, idx: number) => (
              <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Avatar
                  sx={{
                    bgcolor: DESIGN_TOKENS.colors.neutral[100],
                    width: 32,
                    height: 32,
                  }}
                >
                  <Box
                    component="img"
                    src="/milk.svg"
                    alt="Milk"
                    sx={{
                      width: 16,
                      height: 16,
                      filter: 'brightness(0)',
                      opacity: 0.6
                    }}
                  />
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" fontWeight={600} sx={{ color: DESIGN_TOKENS.colors.text.primary }}>
                    {formatDate(record.handling_date)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {record.handling_details?.milk_quantity_liters ? `${record.handling_details.milk_quantity_liters} L` : '-'}
                  </Typography>
                </Box>
              </Box>
            ))}
            
            {hasMore && (
              <Box sx={{ display: 'flex', justifyContent: 'center', pt: 0.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 2 }}>
                  ...
                </Typography>
              </Box>
            )}
          </Stack>
        </Paper>

        {/* Modal Fullscreen Detalhado (Histórico de Ordenha) */}
        <Dialog
          fullScreen
          open={openMilkingFull}
          onClose={handleCloseFull}
          TransitionComponent={Transition}
        >
          <AppBar 
            position="sticky"
            elevation={0}
            sx={{ 
              bgcolor: 'white', 
              color: DESIGN_TOKENS.colors.text.primary,
              borderBottom: `1px solid ${DESIGN_TOKENS.colors.neutral[200]}`,
            }}
          >
            <Toolbar sx={{ px: 1, gap: 1, minHeight: { xs: 56, sm: 64 } }}>
              <IconButton
                edge="start"
                color="inherit"
                onClick={handleCloseFull}
                aria-label="close"
                sx={{ width: 44, height: 44 }}
              >
                <Close />
              </IconButton>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                  {t('dairy:animalMilkingHistory.milkingRecords')}
                </Typography>
                <Typography variant="caption" sx={{ color: DESIGN_TOKENS.colors.text.secondary }}>
                  {records.length} {t('dairy:animalMilkingHistory.records')}
                </Typography>
              </Box>
            </Toolbar>
          </AppBar>
          
          <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: DESIGN_TOKENS.colors.background.default, minHeight: '100vh', pb: 4 }}>
            <Stack spacing={2}>
              {records.map((record: any, idx: number) => (
                <Paper
                  key={idx}
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    border: `1px solid ${DESIGN_TOKENS.colors.neutral[200]}`,
                    bgcolor: 'white',
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar
                        sx={{
                          bgcolor: DESIGN_TOKENS.colors.neutral[100],
                          width: 36,
                          height: 36,
                        }}
                      >
                        <Box
                          component="img"
                          src="/milk.svg"
                          alt="Milk"
                          sx={{
                            width: 20,
                            height: 20,
                            filter: 'brightness(0)',
                            opacity: 0.6
                          }}
                        />
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                          {t('dairy:animalMilkingHistory.milking')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(record.handling_date)}
                        </Typography>
                      </Box>
                    </Box>
                    {record.handling_details?.milk_quantity_liters && (
                      <Chip 
                        label={`${record.handling_details.milk_quantity_liters} L`}
                        size="small"
                        sx={{ fontWeight: 700, bgcolor: DESIGN_TOKENS.colors.primary[50], color: DESIGN_TOKENS.colors.primary[700] }}
                      />
                    )}
                  </Box>

                  <Stack spacing={1}>
                    {record.performed_by && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="caption" color="text.secondary">{t('dairy:animalMilkingHistory.performedBy')}</Typography>
                        <Typography variant="body2">{record.performed_by}</Typography>
                      </Box>
                    )}
                    {record.handling_details?.destination_tank && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="caption" color="text.secondary">{t('dairy:animalMilkingHistory.destinationTank')}</Typography>
                        <Typography variant="body2">{record.handling_details.destination_tank}</Typography>
                      </Box>
                    )}
                    {record.notes && (
                      <Box sx={{ mt: 1, p: 1.5, bgcolor: DESIGN_TOKENS.colors.neutral[50], borderRadius: 1 }}>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>{t('dairy:animalMilkingHistory.notes')}</Typography>
                        <Typography variant="body2" sx={{ fontStyle: 'italic' }}>{record.notes}</Typography>
                      </Box>
                    )}
                  </Stack>
                </Paper>
              ))}
            </Stack>
          </Box>
        </Dialog>
        {renderPassportDialog(passportPayload)}
      </Box>
    );
  }

  // Renderização para Lista de Lotes (Mobile)
  if (type === 'lotList') {
    const lots = data?.data || [];
    const totalAnimals = lots.reduce((acc: number, lot: any) => acc + (lot.cattle_tags?.length || 0), 0);

    const handleOpenLotList = () => setOpenLotListFull(true);
    const handleCloseLotList = () => setOpenLotListFull(false);
    const handleOpenLotDetails = (lot: LotData) => {
      setSelectedLot(lot);
      setLotAnimals([]);
      setLotAnimalsPage(1);
      setOpenLotDetailsFull(true);
    };
    const handleCloseLotDetails = () => setOpenLotDetailsFull(false);

    const totalLotAnimals = lotAnimals.length;
    const totalLotAnimalPages = Math.ceil(totalLotAnimals / lotAnimalsPerPage) || 1;
    const lotAnimalsStartIndex = (lotAnimalsPage - 1) * lotAnimalsPerPage;
    const lotAnimalsEndIndex = lotAnimalsStartIndex + lotAnimalsPerPage;
    const currentLotAnimals = lotAnimals.slice(lotAnimalsStartIndex, lotAnimalsEndIndex);
    const selectedLotAnimalsCount = Array.isArray(selectedLot?.cattle_tags) ? selectedLot?.cattle_tags.length : 0;

    // Resumo compactado (primeiros 3 lotes)
    const compactLots = lots.slice(0, 3);
    const hasMoreLots = lots.length > 3;

    return (
      <Box sx={{ mt: 1.5, width: '100%', maxWidth: '90vw' }}>
        {/* Header Compacto */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, px: 0.5 }}>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
            {t('farm:titles.lot_list', { defaultValue: 'Lista de Lotes' })}
          </Typography>
          <Button 
            size="small" 
            onClick={handleOpenLotList}
            startIcon={<OpenInFull sx={{ fontSize: 14 }} />}
            sx={{ fontSize: '0.7rem', textTransform: 'none', py: 0, minHeight: 0 }}
          >
            {t('common:view_details', { defaultValue: 'Ver detalhes' })}
          </Button>
        </Box>

        {/* Card Resumo */}
        <Paper 
          elevation={0} 
          sx={{ 
            p: 2, 
            border: `1px solid ${DESIGN_TOKENS.colors.neutral[200]}`, 
            borderRadius: 2, 
            bgcolor: 'white'
          }}
        >
          {/* Estatísticas */}
          <Stack direction="row" spacing={2} sx={{ mb: 2, pb: 2, borderBottom: `1px dashed ${DESIGN_TOKENS.colors.neutral[200]}` }}>
             <Box>
                <Typography variant="caption" color="text.secondary" display="block">{t('farm:labels.lots')}</Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1 }}>{lots.length}</Typography>
             </Box>
             <Box>
                <Typography variant="caption" color="text.secondary" display="block">{t('farm:labels.animals')}</Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1 }}>{totalAnimals}</Typography>
             </Box>
          </Stack>

          {/* Lista Compacta */}
          {lots.length === 0 ? (
             <Box sx={{ textAlign: 'center', py: 2 }}>
               <Typography variant="caption" color="text.secondary">
                 {t('farm:labels.no_lots_registered', { defaultValue: 'Nenhum lote registrado ainda' })}
               </Typography>
             </Box>
          ) : (
            <Stack spacing={1.5}>
              {compactLots.map((lot: any) => (
                <Box
                  key={lot._key}
                  onClick={() => handleOpenLotDetails(lot)}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1.5, cursor: 'pointer' }}
                >
                  <Avatar
                    sx={{
                      bgcolor: DESIGN_TOKENS.colors.organization.farm,
                      width: 32,
                      height: 32,
                    }}
                  >
                    <Box component="img" src="/gestores/lote-branco.svg" sx={{ width: 16, height: 16 }} />
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" fontWeight={600} sx={{ color: DESIGN_TOKENS.colors.text.primary }}>
                      {lot.name || t('farm:labels.unnamed_lot')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('farm:labels.animals_count', { count: lot.cattle_tags?.length || 0, defaultValue: `${lot.cattle_tags?.length || 0} animais` })}
                    </Typography>
                  </Box>
                  {lot.is_active && <CheckCircle sx={{ fontSize: 16, color: DESIGN_TOKENS.colors.status.success[500] }} />}
                </Box>
              ))}
              {hasMoreLots && (
                <Typography variant="caption" color="text.secondary" align="center" display="block" sx={{ pt: 0.5 }}>
                  {t('farm:labels.more_lots', { count: lots.length - 3, defaultValue: `+${lots.length - 3} lotes...` })}
                </Typography>
              )}
            </Stack>
          )}
        </Paper>

        {/* Dialog FullScreen (Lista Completa) */}
        <Dialog
          fullScreen
          open={openLotListFull}
          onClose={handleCloseLotList}
          TransitionComponent={Transition}
        >
          <AppBar 
            position="sticky"
            elevation={0}
            sx={{ 
              bgcolor: 'white', 
              color: DESIGN_TOKENS.colors.text.primary,
              borderBottom: `1px solid ${DESIGN_TOKENS.colors.neutral[200]}`,
            }}
          >
            <Toolbar sx={{ px: 1, gap: 1, minHeight: 56 }}>
              <IconButton
                edge="start"
                color="inherit"
                onClick={handleCloseLotList}
                aria-label="close"
              >
                <Close />
              </IconButton>
              <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                {t('farm:titles.lot_list', { defaultValue: 'Lista de Lotes' })}
              </Typography>
            </Toolbar>
          </AppBar>

          <Box sx={{ p: 2, pb: 4, bgcolor: DESIGN_TOKENS.colors.background.default, minHeight: '100%' }}>
             <Stack spacing={2}>
               {/* Cards Completos */}
              {lots.map((lot: any) => (
                 <Paper
                   key={lot._key}
                   elevation={0}
                   sx={{
                     p: 2,
                     borderRadius: 2,
                     border: `1px solid ${DESIGN_TOKENS.colors.neutral[200]}`,
                     bgcolor: 'white',
                    cursor: 'pointer',
                   }}
                  onClick={() => handleOpenLotDetails(lot)}
                 >
                   <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                      <Avatar
                        sx={{
                          bgcolor: DESIGN_TOKENS.colors.organization.farm,
                          width: 40,
                          height: 40,
                        }}
                      >
                        <Box component="img" src="/gestores/lote-branco.svg" sx={{ width: 20, height: 20 }} />
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" fontWeight={700}>
                          {lot.name || t('farm:labels.unnamed_lot')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {t('common:labels.id')}: {lot._key || t('common:no_id')}
                        </Typography>
                      </Box>
                      {lot.is_active && (
                        <Chip
                          label={t('common:status.active')}
                          size="small"
                          color="success"
                          variant="outlined"
                          sx={{ height: 20, fontSize: '0.65rem' }}
                        />
                      )}
                   </Box>
                   
                   <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                    {lot.description || t('common:not_informed')}
                   </Typography>

                   <Divider sx={{ my: 1.5 }} />

                   <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                         <Box component="img" src="/vaca.svg" sx={{ width: 16, height: 16, opacity: 0.5 }} />
                         <Typography variant="caption" fontWeight={600}>
                           {t('farm:labels.animals_count', { count: lot.cattle_tags?.length || 0 })}
                         </Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {lot.created_at ? new Date(lot.created_at).toLocaleDateString() : t('common:not_informed')}
                      </Typography>
                   </Box>
                 </Paper>
               ))}
             </Stack>
          </Box>
        </Dialog>

        <Dialog
          fullScreen
          open={openLotDetailsFull}
          onClose={handleCloseLotDetails}
          TransitionComponent={Transition}
        >
          <AppBar
            position="sticky"
            elevation={0}
            sx={{
              bgcolor: 'white',
              color: DESIGN_TOKENS.colors.text.primary,
              borderBottom: `1px solid ${DESIGN_TOKENS.colors.neutral[200]}`,
            }}
          >
            <Toolbar sx={{ px: 1, gap: 1, minHeight: 56 }}>
              <IconButton
                edge="start"
                color="inherit"
                onClick={handleCloseLotDetails}
                aria-label="close"
              >
                <Close />
              </IconButton>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                  {t('farm:lot.details')}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {selectedLot?.name || t('farm:labels.unnamed_lot')}
                </Typography>
              </Box>
            </Toolbar>
          </AppBar>

          <Box sx={{ p: 2, pb: 4, bgcolor: DESIGN_TOKENS.colors.background.default, minHeight: '100%' }}>
            <Stack spacing={2}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: `1px solid ${DESIGN_TOKENS.colors.neutral[200]}`,
                  bgcolor: 'white',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                  <Avatar
                    sx={{
                      bgcolor: DESIGN_TOKENS.colors.organization.farm,
                      width: 40,
                      height: 40,
                    }}
                  >
                    <Box component="img" src="/gestores/lote-branco.svg" sx={{ width: 20, height: 20 }} />
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" fontWeight={700}>
                      {selectedLot?.name || t('farm:labels.unnamed_lot')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('common:labels.id')}: {selectedLot?._key || t('common:no_id')}
                    </Typography>
                  </Box>
                  {selectedLot?.is_active && (
                    <Chip
                      label={t('common:status.active')}
                      size="small"
                      color="success"
                      variant="outlined"
                      sx={{ height: 20, fontSize: '0.65rem' }}
                    />
                  )}
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {selectedLot?.description || t('common:not_informed')}
                </Typography>
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: `1px solid ${DESIGN_TOKENS.colors.neutral[200]}`,
                  bgcolor: 'white',
                }}
              >
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
                  {t('farm:lot.basic_information')}
                </Typography>
                <Stack spacing={1}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                    <Typography variant="caption" color="text.secondary">{t('farm:lot.name')}</Typography>
                    <Typography variant="body2">{selectedLot?.name || t('farm:labels.unnamed_lot')}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                    <Typography variant="caption" color="text.secondary">{t('farm:lot.id')}</Typography>
                    <Typography variant="body2">{selectedLot?._key || t('common:no_id')}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                    <Typography variant="caption" color="text.secondary">{t('farm:lot.current_paddock')}</Typography>
                    <Typography variant="body2">{selectedLot?.current_paddock_id || t('animal:not_defined')}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                    <Typography variant="caption" color="text.secondary">{t('farm:labels.animals_in_lot')}</Typography>
                    <Typography variant="body2">{t('farm:labels.animals_count', { count: selectedLotAnimalsCount })}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                    <Typography variant="caption" color="text.secondary">{t('farm:labels.created_at')}</Typography>
                    <Typography variant="body2">
                      {selectedLot?.created_at ? new Date(selectedLot.created_at).toLocaleDateString() : t('common:not_informed')}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: `1px solid ${DESIGN_TOKENS.colors.neutral[200]}`,
                  bgcolor: 'white',
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                  <Typography variant="subtitle2" fontWeight={700}>
                    {t('farm:labels.animals_in_lot')}
                  </Typography>
                  <Chip
                    label={t('farm:labels.animals_count', { count: selectedLotAnimalsCount })}
                    size="small"
                    sx={{
                      bgcolor: DESIGN_TOKENS.colors.primary[50],
                      color: DESIGN_TOKENS.colors.primary[700],
                      fontWeight: 600,
                    }}
                  />
                </Box>

                {isLoadingLotAnimals ? (
                  <Typography variant="body2" color="text.secondary">
                    {t('common:status.loading')}
                  </Typography>
                ) : currentLotAnimals.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    {t('farm:labels.no_animals_associated')}
                  </Typography>
                ) : (
                  <Stack spacing={1.5}>
                    {currentLotAnimals.map((animal: AnimalDetails, index: number) => {
                      const rowId = String(animal?._key || animal?._id || animal?.name || animal?.cattle_id?.tag || index);
                      const isLoading = loadingId === rowId;
                      return (
                      <Paper
                        key={rowId}
                        elevation={0}
                        onClick={() => openPassportForAnimal(animal, { closeLotDetails: true })}
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          border: `1px solid ${DESIGN_TOKENS.colors.neutral[200]}`,
                          bgcolor: DESIGN_TOKENS.colors.background.default,
                          cursor: isLoading ? 'progress' : 'pointer',
                          opacity: isLoading ? 0.7 : 1,
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                          <Avatar
                            sx={{
                              bgcolor: DESIGN_TOKENS.colors.neutral[100],
                              width: 36,
                              height: 36,
                            }}
                          >
                            <Box component="img" src="/vaca.svg" sx={{ width: 18, height: 18, opacity: 0.6 }} />
                          </Avatar>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle2" fontWeight={700}>
                              {animal.name || animal.cattle_id?.tag || t('animal:no_name')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {t('animal:table_headers.tag')}: {animal.cattle_id?.tag || t('animal:not_defined')}
                            </Typography>
                          </Box>
                          <Chip
                            label={getStatusLabel(animal.current_status) || t('animal:not_defined')}
                            size="small"
                            color={statusChipColor(animal.current_status)}
                          />
                        </Box>
                        <Stack spacing={0.75}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                            <Typography variant="caption" color="text.secondary">{t('animal:table_headers.breed')}</Typography>
                            <Typography variant="body2">{formatBreed(animal.breed_code)}</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                            <Typography variant="caption" color="text.secondary">{t('animal:table_headers.gender')}</Typography>
                            <Typography variant="body2">{getGenderLabel(animal.gender)}</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                            <Typography variant="caption" color="text.secondary">{t('animal:table_headers.age')}</Typography>
                            <Typography variant="body2">{calculateAgeI18n(animal.birth_date)}</Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                            <Typography variant="caption" color="text.secondary">{t('animal:table_headers.weight')}</Typography>
                            <Typography variant="body2">
                              {animal.birth_weight_kg ? `${animal.birth_weight_kg} ${t('common:units.kg')}` : t('animal:not_defined')}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                            <Typography variant="caption" color="text.secondary">{t('animal:table_headers.location')}</Typography>
                            <Typography variant="body2">{animal.current_paddock_id || t('animal:not_defined')}</Typography>
                          </Box>
                        </Stack>
                      </Paper>
                      );
                    })}
                  </Stack>
                )}

                {totalLotAnimals > lotAnimalsPerPage && (
                  <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" sx={{ mt: 2 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => setLotAnimalsPage((prev) => Math.max(prev - 1, 1))}
                      disabled={lotAnimalsPage === 1}
                    >
                      {t('common:previous')}
                    </Button>
                    <Typography variant="caption" color="text.secondary">
                      {t('common:labels.page_of', { current: lotAnimalsPage, total: totalLotAnimalPages })}
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => setLotAnimalsPage((prev) => Math.min(prev + 1, totalLotAnimalPages))}
                      disabled={lotAnimalsPage === totalLotAnimalPages}
                    >
                      {t('common:next')}
                    </Button>
                  </Stack>
                )}
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: `1px solid ${DESIGN_TOKENS.colors.neutral[200]}`,
                  bgcolor: 'white',
                }}
              >
                <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
                  {t('farm:lot.technical_information')}
                </Typography>
                <Stack spacing={1}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                    <Typography variant="caption" color="text.secondary">{t('farm:lot.document_id')}</Typography>
                    <Typography variant="body2">{selectedLot?._id || t('common:no_id')}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
                    <Typography variant="caption" color="text.secondary">{t('farm:lot.revision')}</Typography>
                    <Typography variant="body2">{selectedLot?._rev || t('common:not_informed')}</Typography>
                  </Box>
                </Stack>
              </Paper>
            </Stack>
          </Box>
        </Dialog>
        {renderPassportDialog(passportPayload)}
      </Box>
    );
  }

  return null;
};
